/**
 * Prepare AI chat markdown so GFM tables and lists render instead of raw pipes.
 */

const TABLE_LINE_RE = /^\s*\|/;
const TABLE_ALIGNMENT_RE = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;
const ABBREV_BEFORE_PERIOD =
  /\b(?:Est|Approx|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec|Mr|Mrs|Ms|Dr|vs|etc|No)\.$/i;

function isTableLine(line) {
  return TABLE_LINE_RE.test(line) || TABLE_ALIGNMENT_RE.test(line);
}

/** True when the model already returned tables, headings, or numbered lists. */
function isStructuredMarkdown(text) {
  if (!text) return false;
  if (/^\s*\|/m.test(text)) return true;
  if (/^\s*\d+\.\s/m.test(text)) return true;
  if (/\*\*[^*\n]+\*\*/.test(text)) return true;
  if (/^#{1,6}\s/m.test(text)) return true;
  return false;
}

/** Collapse runs of 3+ newlines so markdown does not render empty paragraphs. */
export function collapseExtraBlankLines(text) {
  if (!text) return text;
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

function isFollowUpSectionHeader(line) {
  const lower = (line || '').toLowerCase();
  return (
    lower.includes('follow-up questions') ||
    lower.includes('follow-up:') ||
    lower.includes('related questions') ||
    lower.includes('related:') ||
    lower.includes('next steps:')
  );
}

function trimTrailingBlankLines(lines) {
  while (lines.length && lines[lines.length - 1].trim() === '') {
    lines.pop();
  }
}

/** Keep at most one blank line when building markdown output. */
function pushContentLine(result, line) {
  if (line.trim() === '') {
    if (result.length === 0 || result[result.length - 1].trim() === '') {
      return;
    }
  }
  result.push(line);
}

/**
 * Join markdown table rows that were wrapped mid-cell, e.g.
 *   | Service | Est.
 *   MTD Cost | % of Total |
 */
export function repairMarkdownTables(text) {
  if (!text) return text;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    while (
      TABLE_LINE_RE.test(line) &&
      !/\|\s*$/.test(line) &&
      i + 1 < lines.length &&
      lines[i + 1].trim() !== ''
    ) {
      i += 1;
      line = `${line.replace(/\s+$/, '')} ${lines[i].replace(/^\s+/, '')}`;
    }
    out.push(line);
  }
  return out.join('\n');
}

/**
 * Insert paragraph breaks after sentences, without breaking GFM tables or abbreviations.
 */
export function formatMessageContent(text) {
  if (!text) return text;
  if (text.includes('```')) return text;
  const repaired = repairMarkdownTables(text);
  if (isStructuredMarkdown(repaired)) return repaired;
  return repaired
    .split('\n')
    .map((line) => {
      if (isTableLine(line)) return line;
      return line.replace(/([.!?])\s+(?=[A-Z])/g, (match, punct, offset, str) => {
        const before = str.slice(0, offset + 1);
        if (ABBREV_BEFORE_PERIOD.test(before)) return match;
        return `${punct}\n\n`;
      });
    })
    .join('\n');
}

/** Remove standalone markdown horizontal rules; section spacing is handled in CSS. */
export function stripHorizontalRules(text) {
  if (!text) return text;
  const filtered = text
    .split('\n')
    .filter((line) => !/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line))
    .join('\n');
  return collapseExtraBlankLines(filtered);
}

/**
 * Turn agent bold-only lines like "**Top 5 Services:**" into markdown headings
 * so the chat UI can render a clear title/body hierarchy.
 */
export function promoteBoldSectionHeaders(text) {
  if (!text) return text;
  return text
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      const match = trimmed.match(/^\*\*([^*]+)\*\*:?\s*$/);
      if (match) {
        const title = match[1].replace(/:$/, '').trim();
        return `### ${title}`;
      }
      return line;
    })
    .join('\n');
}

/**
 * Strip follow-up blocks (rendered separately) and keep markdown tables intact.
 */
export function preprocessMarkdown(text) {
  if (!text) return '';
  const formatted = promoteBoldSectionHeaders(formatMessageContent(text));
  const lines = formatted.split('\n');
  const result = [];
  let inFollowUp = false;
  for (let i = 0; i < lines.length; i++) {
    if (isFollowUpSectionHeader(lines[i])) {
      trimTrailingBlankLines(result);
      inFollowUp = true;
      continue;
    }
    if (!inFollowUp) {
      pushContentLine(result, lines[i]);
    }
  }
  trimTrailingBlankLines(result);
  return collapseExtraBlankLines(stripHorizontalRules(result.join('\n')));
}
